import React from "react";

import {
  faExclamationTriangle,
  IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";
import { twJoin } from "tailwind-merge";

import { Provider } from "src/api/connectApi/types";
import { BETA_PILL_BODY_TEXTS, BetaPill } from "src/base-components/BetaPill";
import { Icon } from "src/base-components/Icon";
import { Pill } from "src/base-components/Pill";
import { ProviderIcon } from "src/connections/config/Icon";
import { Tooltip, TooltipProps } from "src/design-system/Tooltip";

type ConnectionCardTooltipProps = Omit<TooltipProps, "children"> & {
  icon: IconDefinition;
};

type Props = {
  title: string;
  onClick: () => void;
  isBeta: boolean;
  isDeprecated?: boolean;
  dataLoc?: string;
  provider: Provider | string;
  height: number;
  helpTooltip?: ConnectionCardTooltipProps;
  manifestVersion?: string;
  isLast?: boolean;
  showIcon?: boolean;
  showRegionToolTip?: boolean;
  toolTipRegions?: string[];
};

export const ConnectionCardTooltip: React.FC<ConnectionCardTooltipProps> = (
  props,
) => {
  return (
    <div className="text-gray-500">
      <Tooltip
        body={props.body}
        placement={props.placement}
        title={props.title}
        asChild
      >
        <Icon icon={props.icon} size="md" />
      </Tooltip>
    </div>
  );
};

export const CreateConnectionCard: React.FC<Props> = ({
  title,
  onClick,
  isBeta,
  isDeprecated,
  dataLoc = "",
  provider,
  height,
  helpTooltip,
  manifestVersion,
  isLast = false,
  showIcon = false,
  showRegionToolTip = false,
  toolTipRegions = [],
}) => {
  return (
    <div className={twJoin("w-full", !isLast && "mb-4")}>
      <div
        className="box-border h-full rounded-lg border border-gray-200 transition-all hover:cursor-pointer hover:border-indigo-400"
        data-loc={dataLoc}
        onClick={onClick}
      >
        <div className="flex items-center">
          {showIcon ? (
            <div
              className="mr-2 w-10 pt-2 pl-2"
              style={{ height: `${height}px` }}
            >
              <ProviderIcon
                manifestVersion={manifestVersion}
                provider={provider}
                size="xl"
              />
            </div>
          ) : (
            <div className="pt-2" style={{ height: `${height}px` }} />
          )}
          <div className="flex flex-col pl-2">
            {showRegionToolTip && toolTipRegions.length > 0 ? (
              <Tooltip
                placement="top"
                title={toolTipRegions.join(", ")}
                asChild
              >
                <div className="font-inter-medium-12px text-gray-800">
                  {title}
                </div>
              </Tooltip>
            ) : (
              <div className="font-inter-medium-12px text-gray-800">
                {title}
              </div>
            )}
          </div>
          <div className="ml-auto flex items-center space-x-2 pr-2">
            {isBeta && (
              <BetaPill
                body={BETA_PILL_BODY_TEXTS.connection}
                placement="right"
                title="Beta Connection 🎉"
                asChild
              />
            )}
            {isDeprecated && (
              <Pill size="sm" variant="red">
                <Pill.Icon icon={faExclamationTriangle} />
                <Pill.Text>Deprecated T1</Pill.Text>
              </Pill>
            )}
            {helpTooltip && <ConnectionCardTooltip {...helpTooltip} />}
          </div>
        </div>
      </div>
    </div>
  );
};
